/**
 * Dev-sync J1 — the chart sidebar: season tabs bar + per-path condition table.
 *
 * Port of legacy renderSidebarTable + fitSidebarLabels + the row-selection /
 * tab / collapse handlers (psychrometric_chart.js:1094-1323, :564-605). Two
 * components because the legacy DOM splits them: #psy-sidebar-tabs-bar sits
 * inside the sticky header (#psy-sidebar-sticky-header, next to #psy-legend)
 * while #psy-sidebar-table is the scrolling sibling below it.
 *
 * ★ Row identity is rowKey(component_kind, component_id), NOT section_id
 * (js:1112-1121 + survey risk 9): a UDA shares its host section's
 * section_id, and an EW/PHX bridge appears on BOTH the supply and exhaust
 * paths with the same component_id but different section_ids — selecting one
 * bridge row highlights both of its appearances, exactly like legacy.
 *
 * Scroll preservation: legacy stashes/restores #psy-sidebar-table.scrollTop
 * around every .html() swap (js:1290-1300). React reconciles children inside
 * the SAME container node, so scrollTop is never disturbed — the legacy
 * workaround has no React equivalent to port.
 *
 * J1 is READ-ONLY: the RERATE COMPONENT button jumps to the section on the
 * board (the J1 slice of jumpToSection); the SYNCHRONIZE button renders per
 * the legacy gating but its PATCH/POST write is J5 (survey §E) — inert here.
 * Those two are stale-only, so on dev3 (sync OFF on all units, survey §D2 —
 * every stale set empty) neither can appear.
 *
 * ★ Since 2026-10-16 there is a THIRD button in that same slot, and it is the
 * one you see when nothing is stale: a BLACK `psy-rerate-btn-plain` on any
 * clean row whose component has a rating dialog (rowCanOpenDialog below). It
 * only opens the dialog — no re-rate handoff, no request. So an empty stale
 * set no longer means an empty button slot.
 */

import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type {
  PsySeason,
  PsySeasonFilter,
  PsySidebarPath,
  PsySidebarRow,
  PsyStaleBySeason,
} from './psychrometricApi';
import { DIALOG_RERATE_KINDS, RERATE_KINDS, SYNC_LABELS, UDA_KINDS } from './chartTraces';

// ---------------------------------------------------------------------------
// Row logic (js:1094-1173) — exported for the J5 actions sub-phase.
// ---------------------------------------------------------------------------

/** Fallback only — every row's real label is the backend's component_long
 * (js:1096-1106). electric_heater/humidifier deliberately absent. */
export const KIND_LABELS: Record<string, string> = {
  coil: 'Coil',
  fan: 'Fan',
  gas_heater: 'Gas Heater',
  evap_pack: 'Evap Pack',
  filter: 'Filter',
  eliminator: 'Eliminator',
  silencer: 'Silencer',
  energy_wheel: 'Heat Wheel',
  plate_hex: 'Plate HX',
};

export function fmtRowT(t: number | null | undefined): string {
  return t === null || t === undefined ? '-' : t.toFixed(2);
}

/** js:1119-1121. */
export function rowKey(componentKind: string | null | undefined, componentId: number): string {
  return `${componentKind || ''}:${componentId}`;
}

/** Actionable stale — cond.stale AND cond.needs_rerate (js:1123-1134). */
export function rowNeedsRerate(row: PsySidebarRow, season: PsySeason): boolean {
  const cond = row[season];
  return !!(cond && cond.stale && cond.needs_rerate);
}

/**
 * The non-stale "just open the dialog" case (requested 2026-10-16).
 *
 * Any row whose component HAS a rating/selection dialog — RERATE_KINDS, the
 * backend's own list, which is exactly the seven kinds with a selector — gets
 * a RE-RATE button in the same slot even when nothing is stale. It is styled
 * black rather than red and it only OPENS that dialog: no entering conditions
 * are handed over, so nothing re-rates on arrival and no request is sent (see
 * the popup's openSectionDialog).
 *
 * Deliberately NOT gated on whether a model is already selected — an unrated
 * component is exactly the one an engineer wants the dialog for, and the
 * dialog opens on its own current state either way.
 *
 * Gated on `!cond.stale` rather than on `!rowNeedsRerate` so a row that
 * genuinely needs re-rating keeps exactly the behaviour it has today —
 * including the stale kinds that get SYNCHRONIZE, or no button at all.
 * Per-season like everything else here: a coil stale only in winter still
 * offers the plain black button on the Summer tab.
 */
export function rowCanOpenDialog(row: PsySidebarRow, season: PsySeason): boolean {
  if (!RERATE_KINDS[row.component_kind ?? '']) return false;
  const cond = row[season];
  return !!cond && !cond.stale;
}

/** SYNCHRONIZE label suffix, or null when the kind dialog-rerates instead
 * (js:1136-1142). */
export function syncButtonLabel(row: PsySidebarRow): string | null {
  if (row.component_kind == null) return 'SECTION';
  return SYNC_LABELS[row.component_kind] || null;
}

/** Whether the row actually has something to synchronize for `season` —
 * UDAs always (payload built from the row itself); everything else only when
 * the backend's own field map has an entry (js:1144-1159). */
export function rowSyncable(
  row: PsySidebarRow,
  season: PsySeason,
  staleBySeason: PsyStaleBySeason
): boolean {
  if (row.component_kind && UDA_KINDS[row.component_kind]) return true;
  const entry = (staleBySeason[season] || {})[row.section_id];
  return !!(entry && entry.fields && Object.keys(entry.fields).length);
}

/** Rows not tagged for `season` are left out of that tab entirely
 * (js:1161-1173; backend _row_visible_seasons). */
export function sidebarVisibleRows(path: PsySidebarPath, season: PsySeason): PsySidebarRow[] {
  return path.rows.filter((row) => !row.seasons || row.seasons.indexOf(season) !== -1);
}

/** The selected row scan (js:1186-1197) — first rowKey match wins. */
export function findSelectedRow(
  paths: PsySidebarPath[],
  selectedRowKey: string | null
): PsySidebarRow | null {
  if (selectedRowKey == null) return null;
  for (const path of paths) {
    for (const row of path.rows) {
      if (rowKey(row.component_kind, row.component_id) === selectedRowKey) return row;
    }
  }
  return null;
}

/** The long/short label pair (js:1233-1251): an engineer-typed tag replaces
 * the generic kind name entirely, EXCEPT for UDA-hosted rows whose tag is
 * inherited from the host Plenum — those keep the kind name after the tag. */
function rowLabels(row: PsySidebarRow): { longLabel: string; shortLabel: string } {
  const longKind = row.component_long || KIND_LABELS[row.component_kind ?? ''] || 'Plenum';
  const shortKind = row.component_short || longKind;
  const isUdaHosted = row.component_kind === 'electric_heater' || row.component_kind === 'humidifier';
  const longLabel = row.tag ? (isUdaHosted ? `${row.tag}: ${longKind}` : row.tag) : longKind;
  const shortLabel = row.tag ? (isUdaHosted ? `${row.tag}: ${shortKind}` : row.tag) : shortKind;
  return { longLabel, shortLabel };
}

// ---------------------------------------------------------------------------
// #psy-sidebar-tabs-bar — season tabs + the RERATE/SYNCHRONIZE button slot
// ---------------------------------------------------------------------------

interface SidebarTabsBarProps {
  paths: PsySidebarPath[] | null;
  chartSeason: PsySeasonFilter;
  sidebarSeason: PsySeason;
  selectedRowKey: string | null;
  staleBySeason: PsyStaleBySeason;
  onSeasonTab: (season: PsySeason) => void;
  /** RERATE COMPONENT → jumpToSection (the popup owns the jump). */
  onRerate: (sectionId: number) => void;
  /** The black RE-RATE button (see rowCanOpenDialog) → openSectionDialog:
   * the same jump WITHOUT the re-rate handoff. */
  onOpenDialog: (sectionId: number) => void;
  /** J5 — SYNCHRONIZE → synchronizeRow (the popup owns the POST). */
  onSynchronize: (row: PsySidebarRow) => void;
  /** legacy disables the button for the flight (js:1431/:1445). */
  syncBusy: boolean;
}

export const SidebarTabsBar = ({
  paths,
  chartSeason,
  sidebarSeason,
  selectedRowKey,
  staleBySeason,
  onSeasonTab,
  onRerate,
  onOpenDialog,
  onSynchronize,
  syncBusy,
}: SidebarTabsBarProps) => {
  // Template ships the div empty; renderSidebarTable only runs with data.
  if (!paths) return <div id="psy-sidebar-tabs-bar" />;

  const season = sidebarSeason || 'summer';
  const availableSeasons: PsySeason[] = chartSeason === 'both' ? ['summer', 'winter'] : [chartSeason];
  const selectedRow = findSelectedRow(paths, selectedRowKey);
  // RERATE COMPONENT only for DIALOG_RERATE_KINDS; everything else stale
  // falls through to SYNCHRONIZE (js:1198-1210).
  const showRerateBtn = !!(
    selectedRow &&
    DIALOG_RERATE_KINDS[selectedRow.component_kind ?? ''] &&
    rowNeedsRerate(selectedRow, season)
  );
  let syncLabel: string | null = null;
  if (!showRerateBtn && selectedRow) {
    const selectedCond = selectedRow[season];
    if (selectedCond && selectedCond.stale && rowSyncable(selectedRow, season, staleBySeason)) {
      syncLabel = syncButtonLabel(selectedRow);
    }
  }
  // Nothing stale here at all — offer the plain dialog jump instead. Can
  // never collide with either branch above: both of those need `stale`,
  // this one needs `!stale` (rowCanOpenDialog).
  const showOpenBtn = !!(selectedRow && !showRerateBtn && !syncLabel &&
    rowCanOpenDialog(selectedRow, season));

  return (
    <div id="psy-sidebar-tabs-bar">
      <div className="psy-sidebar-tabs">
        {availableSeasons.map((s) => (
          <button
            type="button"
            key={s}
            className={`psy-sidebar-tab${s === season ? ' active' : ''}`}
            data-season={s}
            onClick={() => {
              // js:570-575 — no-op when already active.
              if (s !== season) onSeasonTab(s);
            }}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>
      {showRerateBtn && selectedRow ? (
        <button
          type="button"
          className="psy-rerate-btn"
          data-section-id={selectedRow.section_id}
          onClick={() => onRerate(selectedRow.section_id)}
        >
          RERATE COMPONENT
        </button>
      ) : syncLabel && selectedRow ? (
        <button
          type="button"
          className="psy-sync-btn"
          data-section-id={selectedRow.section_id}
          data-component-id={selectedRow.component_id}
          data-component-kind={selectedRow.component_kind || ''}
          disabled={syncBusy}
          onClick={() => onSynchronize(selectedRow)}
        >
          SYNCHRONIZE {syncLabel}
        </button>
      ) : showOpenBtn && selectedRow ? (
        <button
          type="button"
          className="psy-rerate-btn psy-rerate-btn-plain"
          data-section-id={selectedRow.section_id}
          onClick={() => onOpenDialog(selectedRow.section_id)}
        >
          RERATE COMPONENT
        </button>
      ) : null}
    </div>
  );
};

// ---------------------------------------------------------------------------
// #psy-sidebar-table — the scrolling per-path tables
// ---------------------------------------------------------------------------

interface SidebarTableProps {
  paths: PsySidebarPath[] | null;
  chartSeason: PsySeasonFilter;
  sidebarSeason: PsySeason;
  selectedRowKey: string | null;
  collapsedPaths: Record<string, boolean>;
  onRowClick: (row: PsySidebarRow) => void;
  onPathToggle: (pathName: string) => void;
}

export const SidebarTable = ({
  paths,
  chartSeason,
  sidebarSeason,
  selectedRowKey,
  collapsedPaths,
  onRowClick,
  onPathToggle,
}: SidebarTableProps) => {
  const season = sidebarSeason || 'summer';

  // ---- fitSidebarLabels (js:1304-1323): .psy-col-label is fixed-width with
  // ellipsis; cells render the LONG label, then any cell actually overflowing
  // swaps down to the short one. Legacy re-measures after every table
  // rebuild; here a measurement pass runs whenever the table's CONTENT config
  // changes (paths/seasons), on the long-label DOM, before paint. Collapse
  // toggles and selection clicks keep the previous fit (same widths — and
  // matching legacy's own display:none no-measure behaviour for collapsed
  // groups). Keyed per path+row because the EW/PHX bridge's rowKey repeats
  // across paths.
  const labelRefs = useRef(new Map<string, HTMLTableCellElement>());
  const fitToken = useMemo(() => ({}), [paths, chartSeason, sidebarSeason]);
  const [fitState, setFitState] = useState<{ token: object | null; keys: Set<string> }>({
    token: null,
    keys: new Set(),
  });
  useLayoutEffect(() => {
    if (fitState.token === fitToken) return;
    const keys = new Set<string>();
    labelRefs.current.forEach((el, key) => {
      if (el && el.scrollWidth > el.clientWidth) keys.add(key);
    });
    setFitState({ token: fitToken, keys });
  }, [fitToken, fitState.token]);
  const fitted = fitState.token === fitToken ? fitState.keys : null;

  if (!paths) return <div id="psy-sidebar-table" className="psy-sidebar-table" />;

  return (
    <div id="psy-sidebar-table" className="psy-sidebar-table">
      {paths.map((path) => {
        const visibleRows = sidebarVisibleRows(path, season);
        if (!visibleRows.length) return null;
        const collapsed = !!collapsedPaths[path.name];
        // Zebra via an explicit counter (js:1228-1231).
        let rowIndex = 0;
        return (
          <div
            key={path.name}
            className={`psy-path-group${collapsed ? ' psy-path-collapsed' : ''}`}
            data-path-name={path.name}
          >
            <div className="psy-path-table-wrap">
              <table className="psy-path-table">
                <thead>
                  {/* Toggles the group open/closed (js:594-605); state lives
                      on collapsedPaths so it survives re-renders. */}
                  <tr className="psy-path-thead-row" onClick={() => onPathToggle(path.name)}>
                    <th className="psy-col-label">{path.name}</th>
                    <th className="psy-col-num">EADB</th>
                    <th className="psy-col-num">EAWB</th>
                    <th className="psy-col-num psy-col-leaving">LADB</th>
                    <th className="psy-col-num psy-col-leaving">LAWB</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => {
                    const key = rowKey(row.component_kind, row.component_id);
                    const cellKey = `${path.name}|${key}`;
                    const { longLabel, shortLabel } = rowLabels(row);
                    const useShort = !!fitted && fitted.has(cellKey);
                    const label = useShort ? shortLabel : longLabel;
                    const cond = row[season];
                    const ent = (cond && cond.entering) || { t_db: null, t_wb: null };
                    const leav = (cond && cond.leaving) || { t_db: null, t_wb: null };
                    // Only this season's row turns red when only this season
                    // is flagged (js:1252-1258).
                    const isSelected = selectedRowKey != null && key === selectedRowKey;
                    const rowCls =
                      'psy-table-row' +
                      (rowIndex % 2 ? ' psy-table-row-alt' : '') +
                      (cond && cond.stale ? ' psy-table-row-stale' : '') +
                      (isSelected ? ' psy-table-row-selected' : '');
                    rowIndex += 1;
                    return (
                      <tr
                        key={cellKey}
                        className={rowCls}
                        data-section-id={row.section_id}
                        data-component-id={row.component_id}
                        data-component-kind={row.component_kind || ''}
                        onClick={() => onRowClick(row)}
                      >
                        <td
                          className="psy-col-label"
                          data-short-label={shortLabel}
                          title={label}
                          ref={(el) => {
                            if (el) labelRefs.current.set(cellKey, el);
                            else labelRefs.current.delete(cellKey);
                          }}
                        >
                          {label}
                        </td>
                        <td className="psy-col-num">{fmtRowT(ent.t_db)}</td>
                        <td className="psy-col-num">{fmtRowT(ent.t_wb)}</td>
                        {/* `psy-col-leaving` is what keeps a stale row's
                            LEAVING pair out of the red (requested
                            2026-10-16) — ahuBoardOverrides.scss. The label
                            and the entering pair still redden. */}
                        <td className="psy-col-num psy-col-leaving">{fmtRowT(leav.t_db)}</td>
                        <td className="psy-col-num psy-col-leaving">{fmtRowT(leav.t_wb)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
};
